<template>
	<button
		v-if="pendingRelease"
		disabled
		:type="btnType"
		:class="['uppercase', btnClass]"
		:aria-label="pendingReleaseText"
	>
		<span v-html="pendingReleaseText" />
	</button>
	<button
		v-else-if="unavailable"
		:class="[
			'w-full h-12 min-w-40 !py-5 bg-black text-white',
			'cursor-not-allowed opacity-25',
			'text-xs/[12px] font-medium uppercase text-center tracking-widest',
			btnClass,
		]"
		disabled
		aria-label="Unavailable"
	>
		Unavailable
	</button>
	<button
		v-else-if="alt"
		@click.prevent="handleOnClick"
		:disabled="disabled"
		:type="btnType"
		:aria-label="btnText"
		:class="[
			'cursor-pointer !text-black',
			'inline-flex relative justify-center items-center text-nowrap',
			'text-xs/[12px] font-medium underline text-center',
			blocked && '!cursor-not-allowed opacity-25',
			btnClass,
			loading && 'loading',
		]"
	>
		<slot name="text">
			<span v-if="loading">Adding</span>
			<span v-else>{{ btnText }}</span>
		</slot>
	</button>
	<button
		v-else
		@click.prevent="handleOnClick"
		:disabled="disabled"
		:type="btnType"
		:aria-label="btnText"
		:class="[
			'cursor-pointer w-full h-12 min-w-40 !py-5 bg-black text-white',
			'inline-flex relative justify-center items-center',
			'text-xs/[12px] font-medium uppercase text-center tracking-widest',
			'transition-all duration-300 rounded-none',
			blocked && '!cursor-not-allowed opacity-25',
			btnClass,
			loading && 'loading',
		]"
	>
		<slot name="text">
			<span v-if="loading" class="w-6 h-6 border-[3px] border-l-neutral-400 rounded-full animate-spin" />
			<span v-else>{{ btnText }}</span>
		</slot>
	</button>
</template>
<script setup>
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'

import { useSessionStore, useCartStore } from '@stores'

const props = defineProps({
	pendingRelease: {
		type: Boolean,
		default: false,
	},
	pendingReleaseText: {
		type: String,
		default: 'Not Available',
	},
	allowInEnroll: {
		type: Boolean,
		default: false,
	},
	itemId: {
		type: [String, Number],
		required: true,
	},
	kit: {
		type: Boolean,
		default: false,
	},
	btnStartingText: {
		type: String,
		default: 'Add to Bag',
	},
	btnType: {
		type: String,
		default: 'button',
	},
	btnClass: {
		type: String,
		default: '',
	},
	cannotPurchase: {
		type: Boolean,
		default: false,
	},
	outOfStock: {
		type: Boolean,
		default: false,
	},
	notAvailable: {
		type: Boolean,
		default: false,
	},
	discountType: {
		type: String,
	},
	quantity: {
		type: Number,
		default: 1,
	},
	alt: {
		type: Boolean,
		default: false,
	},
})

const btnStatus = ref('idle')
const loading = ref(false)

const { addToCart } = useCartStore()
const { isEnrollContext } = storeToRefs(useSessionStore())

const emit = defineEmits(['adding', 'loading', 'added'])

const unavailable = computed(() => isEnrollContext.value && !props.allowInEnroll)
const disabled = computed(() => props.cannotPurchase || props.outOfStock || loading.value)
const blocked = computed(() => props.cannotPurchase || props.outOfStock)
const btnText = computed(() => {
	if (props.notAvailable) {
		return 'Not Available'
	}
	if (props.outOfStock) {
		return 'Out of Stock'
	}
	switch (btnStatus.value) {
		case 'idle':
			return props.btnStartingText
		case 'success':
			return 'Added'
		case 'prevent':
			return 'Not Allowed'
		case 'fail':
			return 'Failed'
		default:
			return null
	}
})
const isKit = computed(() => props.kit === 1 || props.kit === true)

const handleOnClick = async () => {
	try {
		emit('loading', true)
		loading.value = true
		const payload = {
			id: props.itemId,
			quantity: props.quantity,
			isKit: isKit.value,
		}
		if (props.discountType) {
			payload.discountType = props.discountType
		}
		const items = [payload]
		await addToCart({ items })
		emit('added', payload)
		btnStatus.value = 'success'
		setTimeout(() => {
			btnStatus.value = 'idle'
		}, 1200)
	} catch (_) {
		btnStatus.value = 'fail'
		setTimeout(() => {
			btnStatus.value = 'idle'
		}, 1200)
	} finally {
		emit('loading', false)
		loading.value = false
	}
}
</script>
